import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Route, Clock, Navigation } from "lucide-react";
import { api } from "../lib/api";
import { logWarn } from "../lib/log";

const pin = (color, size = 18) => L.divIcon({ className: "velocity-pin", iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};box-shadow:0 0 0 3px rgba(11,13,18,.9),0 0 12px ${color}"></div>` });
const PICK = pin("#34d399"), DROP = pin("#22d3ee"), STOP = pin("#a78bfa", 14);
const DRIVER = L.divIcon({ className: "velocity-pin", iconSize: [26, 26], iconAnchor: [13, 13],
  html: `<div style="width:26px;height:26px;border-radius:50%;background:#f59e0b;display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 4px rgba(245,158,11,.25),0 0 16px #f59e0b;animation:pulse 1.5s infinite"><svg width="14" height="14" viewBox="0 0 24 24" fill="#0B0D12"><path d="M12 2 4 20l8-4 8 4z"/></svg></div>` });

export default function JobRouteMap({ job, track = false }) {
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!track || !["claimed", "picked_up"].includes(job.status)) return;
    let t; const poll = async () => { try { const { data } = await api.get(`/gigs/jobs/${job.job_id}/location`); setLive(data); } catch (e) { logWarn("location poll failed", e?.response?.status || e?.message); } t = setTimeout(poll, 10000); };
    poll(); return () => clearTimeout(t);
  }, [track, job.job_id, job.status]);

  if (job.pickup_lat == null || job.dropoff_lat == null) return null;
  const a = [job.pickup_lat, job.pickup_lng], b = [job.dropoff_lat, job.dropoff_lng];
  const stops = (job.stops || []).filter((s) => s.lat != null).map((s) => [s.lat, s.lng]);
  const pts = [a, b, ...stops];
  const loc = live?.location;
  if (loc) pts.push([loc.lat, loc.lng]);
  const bounds = L.latLngBounds(pts).pad(0.25);
  const ago = loc ? Math.max(0, Math.round((Date.now() - new Date(loc.at).getTime()) / 60000)) : null;
  return (
    <div className="rounded-xl overflow-hidden border border-slate-800 relative" data-testid={`job-map-${job.job_id}`}>
      <MapContainer key={pts.length} bounds={bounds} style={{ height: track ? 190 : 150, width: "100%", background: "#0B0D12" }} zoomControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false} attributionControl={false}>
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" className="map-dark-tiles" />
        <Polyline positions={[a, b, ...stops]} pathOptions={{ color: "#22d3ee", weight: 2, dashArray: "6 6", opacity: 0.8 }} />
        {live?.trail?.length > 1 && <Polyline positions={live.trail.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#f59e0b", weight: 3, opacity: 0.7 }} />}
        <Marker position={a} icon={PICK} /><Marker position={b} icon={DROP} />
        {stops.map((s, i) => <Marker key={`${s[0]},${s[1]}`} position={s} icon={STOP}><Tooltip permanent direction="top" className="!bg-[#0B0D12] !border-slate-700 !text-slate-200 !text-[10px] !px-1 !py-0">Stop {i + 1}</Tooltip></Marker>)}
        {loc && <Marker position={[loc.lat, loc.lng]} icon={DRIVER} />}
      </MapContainer>
      <div className="absolute bottom-2 left-2 flex gap-2 text-[11px] font-mono flex-wrap">
        {job.distance_miles != null && <>
          <span className="px-2 py-0.5 rounded-full bg-[#0B0D12]/90 border border-slate-700 text-slate-200 flex items-center gap-1" data-testid={`job-route-stats-${job.job_id}`}><Route className="w-3 h-3 text-cyan-400" />{job.distance_miles} mi</span>
          <span className="px-2 py-0.5 rounded-full bg-[#0B0D12]/90 border border-slate-700 text-slate-200 flex items-center gap-1"><Clock className="w-3 h-3 text-emerald-400" />{job.drive_minutes} min drive</span>
        </>}
        {track && live?.live && (
          <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 flex items-center gap-1" data-testid={`job-live-${job.job_id}`}>
            <Navigation className="w-3 h-3" />{loc ? `${live.driver_name?.split(" ")[0] || "Driver"} · ${ago === 0 ? "live" : `${ago} min ago`}${loc.speed_mph != null ? ` · ${Math.round(loc.speed_mph)} mph` : ""}` : "Waiting for driver GPS…"}
          </span>
        )}
      </div>
    </div>
  );
}
